<template>
  <div>
    <h3 class="uk-margin-remove-bottom mp-color-console">Parameters</h3>
    <p class="uk-text-muted uk-text-small uk-margin-remove-top uk-margin-bottom">* indicates a required parameter</p>

    <div class="uk-position-relative">

      <div v-if="isRequestInFlight" class="uk-position-absolute uk-height-1-1 uk-width-1-1 uk-flex uk-flex-middle uk-flex-center mp-overlay-spinner">
        <i class="fas fa-circle-notch fa-spin uk-text-large"></i>
      </div>

      <fieldset class="uk-fieldset" :disabled="endpoint.disabled">
        <div class="uk-grid uk-child-width-1-2@l uk-child-width-1-1 uk-grid-small" uk-grid>
          <div v-for="(parameter, i) in endpoint.parameters" :key="i" :class="parameter.class">
            <label class="uk-form-label">{{ getParameterLabel(parameter) }} <span v-if="parameter.required">*</span></label>
            <div class="uk-form-controls">

              <!-- select -->
              <v-select v-if="parameter.type === 'select'" :options="parameter.values" :value="requestParams[parameter.name]" @input="$emit(`update:${parameter.name}`, $event)">
                <template v-if="parameter.required" #search="{attributes, events}">
                  <input
                      class="vs__search"
                      :required="!requestParams[parameter.name]"
                      v-bind="attributes"
                      v-on="events"
                  />
                </template>
              </v-select>

              <!-- 'country' select -->
              <v-select v-else-if="parameter.type === 'country'" :options="schema.types.country.values" :value="requestParams[parameter.name]" @input="$emit(`update:${parameter.name}`, $event)" :reduce="value => value.code">
                <template v-if="parameter.required" #search="{attributes, events}">
                  <input
                      class="vs__search"
                      :required="!requestParams[parameter.name]"
                      v-bind="attributes"
                      v-on="events"
                  />
                </template>
              </v-select>

              <!-- 'includes' multiselect -->
              <v-select v-else-if="parameter.type === 'includes'" :options="schema.types.includes.values" :value="requestParams[parameter.name]" @input="$emit(`update:${parameter.name}`, $event)" :multiple="true">
                <template v-if="parameter.required" #search="{attributes, events}">
                  <input
                      class="vs__search"
                      :required="requestParams[parameter.name] !== '' ? requestParams[parameter.name].length === 0 : true"
                      v-bind="attributes"
                      v-on="events"
                  />
                </template>
              </v-select>

              <!-- 'multiselect' -->
              <v-select v-else-if="parameter.type === 'multiselect'" :options="parameter.values" :value="requestParams[parameter.name]" @input="$emit(`update:${parameter.name}`, $event)" :multiple="true">
                <template v-if="parameter.required" #search="{attributes, events}">
                  <input
                      class="vs__search"
                      :required="requestParams[parameter.name] !== '' ? requestParams[parameter.name].length === 0 : true"
                      v-bind="attributes"
                      v-on="events"
                  />
                </template>
              </v-select>

              <!-- 'point' -->
              <div v-else-if="parameter.type === 'point'" uk-grid class="uk-grid uk-grid-small uk-child-width-1-2">
                <div>
                  <input :class="`uk-input uk-border-rounded`" :name="`${parameter.name}.latitude`" type="number" placeholder="e.g. 51.5166907" step="0.0000001" :value="requestParams[parameter.name].latitude" @input="$emit(`update:${parameter.name}`, Object.assign(requestParams[parameter.name], {latitude: $event.target.value}))" :required="parameter.required">
                </div>
                <div>
                  <input :class="`uk-input uk-border-rounded`" :name="`${parameter.name}.longitude`" type="number" placeholder="e.g. -0.1293059" step="0.0000001" :value="requestParams[parameter.name].longitude" @input="$emit(`update:${parameter.name}`, Object.assign(requestParams[parameter.name], {longitude: $event.target.value}))" :required="parameter.required">
                </div>
              </div>

              <!-- datetime -->
              <datetime v-else-if="parameter.type === 'datetime'" type="datetime" input-class="uk-input uk-border-rounded" class="theme-rp" v-model="requestParams[parameter.name]"></datetime>

              <!-- 'boolean' -->
              <input v-else-if="parameter.type === 'boolean'" style="height: 30px; width: 30px; margin-top: 5px;" class="uk-checkbox uk-border-rounded" type="checkbox" :checked="requestParams[parameter.name]" value="true" @change="$emit(`update:${parameter.name}`, $event.target.checked)">

              <!-- integer -->
              <input v-else-if="parameter.type === 'integer'" :class="`uk-input uk-border-rounded`" :name="parameter.name" type="number" step="1" min="1" :placeholder="parameter.placeholder" :value="requestParams[parameter.name]" @input="$emit(`update:${parameter.name}`, $event.target.value)" :required="parameter.required">

              <!-- double -->
              <input v-else-if="parameter.type === 'double'" :class="`uk-input uk-border-rounded`" :name="parameter.name" type="number" step="0.0000001" min="-90" :placeholder="parameter.placeholder" :value="requestParams[parameter.name]" @input="$emit(`update:${parameter.name}`, $event.target.value)" :required="parameter.required">

              <!-- open -->
              <textarea v-else-if="parameter.type === 'open'" :class="`uk-textarea uk-border-rounded`" :value="requestParams[parameter.name]" :name="parameter.name" @input="$emit(`update:${parameter.name}`, $event.target.value)" :required="parameter.required"></textarea>

              <!-- Text input -->
              <input v-else :class="`uk-input uk-border-rounded`" :name="parameter.name" type="text" :placeholder="parameter.placeholder ? parameter.placeholder : ''" :value="requestParams[parameter.name]" @input="$emit(`update:${parameter.name}`, $event.target.value)" :required="parameter.required">

            </div>
            <p v-if="parameter.hint" class="uk-text-muted uk-text-small" style="margin-top: 5px;">{{ parameter.hint }}</p>
          </div>
        </div>
      </fieldset>

    </div>
  </div>
</template>

<script>
  export default {
    name: "EndpointParameters",
    props: ["endpoint", "requestParams", "isRequestInFlight", "schema"], // prop feeds down, emit + sync pipes up,

    methods: {
      /**
       * Get the parameter's label - either its label property, the label property of its type, or its name
       *
       * @param parameter
       * @returns {string} The parameter label
       */
      getParameterLabel: function(parameter) {
        if(parameter.label !== undefined){
          return parameter.label
        } else if(this.schema.types[parameter.type]?.label !== undefined){
          return this.schema.types[parameter.type].label
        } else {
          return parameter.name
        }
      }
    }
  }
</script>

<style lang="scss">
  .theme-rp .vdatetime-popup__header,
  .theme-rp .vdatetime-calendar__month__day--selected > span > span,
  .theme-rp .vdatetime-calendar__month__day--selected:hover > span > span {
    background: #166d8a;
  }

  .theme-rp .vdatetime-year-picker__item--selected,
  .theme-rp .vdatetime-time-picker__item--selected,
  .theme-rp .vdatetime-popup__actions__button {
    color: #166d8a;
  }
  .mp-overlay-spinner {
    top: 0;
    left: 0;
    background-color: rgba(255,255,255,0.5);
    z-index: 2;
  }
</style>
